/* Campaign cart mutations are serialized. No Admin API credentials belong in the theme. */
(function (root) {
  class NoveltyCart {
    constructor({ fetcher, state, rootPath = '/', changed = () => {} }) {
      this.fetcher = fetcher; this.state = state; this.root = rootPath; this.changed = changed; this.tail = Promise.resolve();
    }
    enqueue(job) { const next = this.tail.then(job); this.tail = next.catch(() => {}); return next; }
    async request(path, body) {
      const response = await this.fetcher(this.root + path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : { cache: 'no-store' });
      if (!response.ok) { const e = new Error('カートを更新できませんでした。再度お試しください。'); e.status = response.status; throw e; }
      return response.json();
    }
    async synchronize(retryInventory = true) {
      let cart = await this.request('cart.js');
      const state = await this.state();
      if (!Array.isArray(state.campaigns) || !Array.isArray(state.giftIds)) throw new Error('ギフト設定を確認できませんでした。');
      const gifts = new Set(state.giftIds.map(String));
      const merchandise = cart.items.filter(i => !gifts.has(String(i.variant_id)) && i.quantity > 0);
      const desired = new Map(state.giftIds.map(id => [String(id), { quantity: 0, campaign: null }]));
      for (const c of state.campaigns) {
        const active = c.enabled && state.now >= c.start && state.now < c.end;
        const eligible = active && merchandise.some(i => c.target.kind === 'all' || c.target.variantIds.map(String).includes(String(i.variant_id)));
        const entitled = eligible ? c.quantity : 0;
        const quantity = Math.min(entitled, Math.max(0, c.available), c.cap);
        if (c.shortage === 'hold' && quantity < entitled) throw new Error('ギフトの在庫が不足しています。お問い合わせください。');
        desired.set(String(c.giftId), { quantity, campaign: c.id });
      }
      let changed = false;
      // Remove expired/paused campaign gifts before adding any new campaign gift.
      for (const [id, target] of desired) {
        let keep = target.quantity;
        for (const line of cart.items.filter(i => String(i.variant_id) === id)) {
          const quantity = Math.min(line.quantity, keep); keep -= quantity;
          if (line.quantity !== quantity) { cart = await this.request('cart/change.js', { id: line.key, quantity }); changed = true; }
        }
      }
      for (const [id, target] of desired) {
        const existing = cart.items.filter(i => String(i.variant_id) === id).reduce((n, i) => n + i.quantity, 0);
        const missing = target.quantity - existing;
        if (missing <= 0) continue;
        try {
          await this.request('cart/add.js', { items: [{ id: Number(id), quantity: missing, properties: { _mhl_novelty_campaign: target.campaign } }] });
          cart = await this.request('cart.js'); changed = true;
        } catch (e) {
          // A 422 can partially fill available stock. Re-read both cart and settings once.
          if (e.status === 422 && retryInventory) return this.synchronize(false);
          throw e;
        }
      }
      cart = await this.request('cart.js');
      for (const [id, target] of desired) {
        const lines = cart.items.filter(i => String(i.variant_id) === id);
        if (lines.some(i => typeof i.final_line_price === 'number' && i.final_line_price !== 0)) throw new Error('ギフトの価格設定を確認できませんでした。お問い合わせください。');
        const actual = lines.reduce((n, i) => n + i.quantity, 0);
        if (actual !== target.quantity) throw new Error('ギフトの数量を確認できませんでした。カートを再読み込みしてください。');
      }
      if (changed) this.changed(cart);
      return cart;
    }
    sync() { return this.enqueue(() => this.synchronize()); }
    checkout(note, updates) {
      return this.enqueue(async () => {
        const body = {};
        if (typeof note === 'string') body.note = note;
        if (updates && Object.keys(updates).length) body.updates = updates;
        if (Object.keys(body).length) await this.request('cart/update.js', body);
        return this.synchronize();
      });
    }
    fetch(url, options) {
      // Capture the current line key before another queued mutation can reorder the cart.
      const original = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
      const key = original?.line && typeof document !== 'undefined' ? document.querySelector(`[data-novelty-line="${original.line}"]`)?.dataset.noveltyKey : null;
      return this.enqueue(async () => {
        let body = original;
        if (key) { body = { ...original, id: key }; delete body.line; options = { ...options, body: JSON.stringify(body) }; }
        const response = await this.fetcher(url, options); if (!response.ok) return response;
        const initial = await response.clone().json(); if (initial.status || initial.errors) return response;
        // The merchandise mutation has already succeeded. Do not present a gift failure
        // as an add failure: return a renderable cart and keep checkout reconciliation.
        let cart = initial; let noveltyError;
        try { cart = await this.synchronize(); }
        catch (error) {
          noveltyError = '商品はカートに反映済みです。ギフトの確認ができませんでした。購入手続き前にカートを再読み込みしてください。';
          try { cart = await this.request('cart.js'); } catch (_) { /* Keep the successful mutation response. */ }
        }
        const sectionInput = typeof body?.get === 'function' ? body.get('sections') : body?.sections;
        let sections = initial.sections;
        if (sectionInput) {
          const ids = Array.isArray(sectionInput) ? sectionInput.join(',') : sectionInput;
          const sectionUrl = typeof body?.get === 'function' ? body.get('sections_url') : body?.sections_url;
          const path = sectionUrl || this.root;
          try {
            const r = await this.fetcher(path + (path.includes('?') ? '&' : '?') + 'sections=' + encodeURIComponent(ids), { cache: 'no-store' });
            if (!r.ok) throw new Error('section_fetch_failed');
            const fresh = await r.json();
            if (Object.values(fresh).some(value => value === null)) throw new Error('section_render_failed');
            sections = fresh;
          } catch (_) {
            noveltyError = '商品はカートに反映済みです。最新の表示を確認するため、カートを再読み込みしてください。';
          }
        }
        const result = /\/cart\/add(?:\.js)?(?:\?|$)/.test(url) ? { ...initial, sections } : { ...cart, sections };
        return new Response(JSON.stringify({ ...result, noveltyError }), { status: 200, headers: { 'Content-Type': 'application/json' } });
      });
    }
  }
  root.MhlNoveltyCart = NoveltyCart;
  if (typeof module !== 'undefined') module.exports = { NoveltyCart };
})(typeof window !== 'undefined' ? window : globalThis);
